import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { patientsApi } from '@/api/patients';
import { reportsApi } from '@/api/reports';
import { Add, Profile2User } from '@/components/icons';
import AddPatientFlowModal from '@/components/patients/AddPatientFlowModal';
import IntakeSurveyLinkModal from '@/components/patients/IntakeSurveyLinkModal';
import PatientCreatedAssignModal from '@/components/patients/PatientCreatedAssignModal';
import { ReportData } from '@/components/preview/types';
import { CardGrid, EmptyState, ListSection, SearchField } from '@/components/system';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useModalContext } from '@/components/ui/modal-provider';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { useDebounce } from '@/hooks/use-debounce';
import { DashboardPagination } from '@/pages/dashboard/components/DashboardPagination';
import { Patient } from '@/types/patient';
import PatientCard from '../components/patients/PatientCard';
import PatientDialog from '../components/patients/PatientDialog';
import WrapperLayout from './components/WrapperLayout';

export default function Patients() {
    const { openModal, openWizardModal, closeModal } = useModalContext();
    const confirmDelete = useConfirmDelete();
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [sortBy, setSortBy] = useState<'name' | 'recent'>('recent');
    const [currentPage, setCurrentPage] = useState(1);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
    // Only the "Inserir manualmente" path has a screen to go back to.
    const [cameFromAddFlow, setCameFromAddFlow] = useState<boolean>(false);
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const normalizedSearchQuery = debouncedSearchQuery.trim();
    const hasSearchQuery = searchQuery.trim().length > 0;

    const { data: patientsResponse, isLoading } = useQuery({
        queryKey: ['patients', 'list', currentPage, normalizedSearchQuery, sortBy],
        queryFn: () =>
            patientsApi.list({
                page: currentPage,
                pageSize: 10,
                q: normalizedSearchQuery || undefined,
                sort: sortBy,
            }),
    });

    const { data: reports } = useQuery<ReportData[]>({
        queryKey: ['reports', 'list'],
        queryFn: () =>
            reportsApi.listAll({
                ordering: '-updated_at',
            }),
        initialData: [],
    });

    const ongoingReportsMap = useMemo(() => {
        const map = new Map<string, ReportData>();
        reports.forEach((report) => {
            if (report.patient?.id && report.status === 'in_progress') {
                if (!map.has(report.patient.id)) map.set(report.patient.id, report);
            }
        });
        return map;
    }, [reports]);

    const { mutate: deletePatient } = useMutation({
        mutationFn: (id: string) => patientsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['patients'] });
        },
    });

    const patients = patientsResponse?.data ?? [];
    const pagination = patientsResponse?.pagination;

    const totalPages = pagination?.totalPages ?? 0;
    const totalPatients = pagination?.totalCount ?? patients.length;
    const shouldShowPagination = totalPages > 1;

    const handleEdit = (patient: Patient) => {
        setCameFromAddFlow(false);
        setEditingPatient(patient);
        setIsDialogOpen(true);
    };

    const handleDelete = (patient: Patient) => {
        confirmDelete({
            title: `Excluir paciente?`,
            description: `Você está prestes a excluir ${patient.full_name}. Todos os laudos associados a este paciente também serão excluídos permanentemente. Essa ação não poderá ser desfeita.`,
            confirmText: 'Sim, deletar',
            onConfirm: (close) => {
                deletePatient(patient.id);
                close();
            },
        });
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingPatient(null);
        setCameFromAddFlow(false);
    };

    const openAddPatientFlow = () => {
        setEditingPatient(null);
        openWizardModal({
            content: (
                <AddPatientFlowModal
                    onManual={() => {
                        closeModal();
                        setCameFromAddFlow(true);
                        setIsDialogOpen(true);
                    }}
                    onSendSurveyLink={(patientName) => {
                        openModal({
                            content: <IntakeSurveyLinkModal patientName={patientName} />,
                            size: 'md',
                            footer: {
                                closeText: 'Fechar',
                            },
                        });
                    }}
                />
            ),
            size: 'lg',
        });
    };

    const handleBackToAddFlow = () => {
        handleDialogClose();
        openAddPatientFlow();
    };

    const handlePatientCreated = (createdPatient: Patient) => {
        handleDialogClose();
        openWizardModal({
            content: <PatientCreatedAssignModal patient={createdPatient} />,
            size: 'md',
        });
    };

    return (
        <WrapperLayout
            title="Pacientes"
            leftIcon={Profile2User}
            meta={
                isLoading || totalPatients === 0 ? undefined : (
                    <span aria-live="polite">{totalPatients}</span>
                )
            }
            actions={
                <Button onClick={openAddPatientFlow} className="w-full justify-center sm:w-auto">
                    <Add className="size-4" />
                    Novo paciente
                </Button>
            }
            toolbar={{
                search: (
                    <SearchField
                        className="w-full"
                        placeholder="Buscar pacientes por nome ou email..."
                        value={searchQuery}
                        onValueChange={(value) => {
                            setSearchQuery(value);
                            setCurrentPage(1);
                        }}
                    />
                ),
                controls: (
                    <Select
                        value={sortBy}
                        onValueChange={(value: string) => {
                            setSortBy(value as 'name' | 'recent');
                            setCurrentPage(1);
                        }}
                    >
                        <SelectTrigger className="w-52 shrink-0">
                            <SelectValue placeholder="Ordenar por" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="name">Nome (A–Z)</SelectItem>
                            <SelectItem value="recent">Mais recentes</SelectItem>
                        </SelectContent>
                    </Select>
                ),
            }}
        >
            <ListSection contentClassName="p-4 sm:p-6">
                {isLoading ? (
                    <Loading className="h-auto py-8" size={160} />
                ) : patients.length === 0 ? (
                    <EmptyState
                        icon={Profile2User}
                        title={
                            hasSearchQuery ? 'Nenhum paciente encontrado' : 'Nenhum paciente ainda'
                        }
                        description="Crie um registro para acompanhar laudos, formulários e instrumentos do paciente em um só lugar."
                        action={
                            <Button
                                onClick={openAddPatientFlow}
                                className="w-full justify-center sm:w-auto"
                            >
                                <Add className="size-4" />
                                {hasSearchQuery
                                    ? 'Adicionar novo paciente'
                                    : 'Adicionar seu primeiro paciente'}
                            </Button>
                        }
                    />
                ) : (
                    <div className="space-y-4 sm:space-y-6">
                        <CardGrid>
                            {patients.map((patient) => (
                                <PatientCard
                                    key={patient.id}
                                    patient={patient}
                                    ongoingReport={ongoingReportsMap.get(patient.id)}
                                    onEdit={() => handleEdit(patient)}
                                    onDelete={() => handleDelete(patient)}
                                />
                            ))}
                        </CardGrid>

                        {shouldShowPagination ? (
                            <DashboardPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                totalReports={totalPatients}
                                itemsPerPage={pagination?.pageSize ?? 10}
                                itemLabel="pacientes"
                            />
                        ) : null}
                    </div>
                )}
            </ListSection>

            <PatientDialog
                open={isDialogOpen}
                onOpenChange={handleDialogClose}
                patient={editingPatient}
                onPatientCreated={handlePatientCreated}
                onBack={cameFromAddFlow ? handleBackToAddFlow : undefined}
            />
        </WrapperLayout>
    );
}
